import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Chip,
  Card,
  Stack,
  IconButton,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import { withdrawRecord } from '../api/modules/payment';
import { showMessage } from '../utils';
import { isInApp } from '../utils/environment';
import { getStatusLabel, formatTime, formatAmount, getDateTypeFromFilter } from '../utils/withdrawUtils';

// 自定义样式组件
const SearchBar = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F3F4F6',
    border: 'none',
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      padding: '12px 16px 12px 0',
      fontSize: '15px',
      color: '#6B7280',
      '&::placeholder': {
        color: '#9CA3AF',
      }
    }
  }
});

const FilterChip = styled(Chip)(({ theme, ...props }) => {
  const isActive = props.active === 'true' || props.active === true;
  return {
    backgroundColor: isActive ? '#000000' : '#F3F4F6',
    color: isActive ? '#FFFFFF' : '#64748B',
    fontWeight: isActive ? 600 : 500,
    fontSize: '14px',
    height: '32px',
    borderRadius: '16px',
    '&:hover': {
      backgroundColor: isActive ? '#333333' : '#E5E7EB',
    },
  };
});

const StatusChip = styled(Chip)(({ status }) => {
  let backgroundColor, color;

  switch (status) {
    case 0: // 待审核
      backgroundColor = '#FEF3C7';
      color = '#D97706';
      break;
    case 1: // 已审核
      backgroundColor = '#DBEAFE';
      color = '#2563EB';
      break;
    case 2: // 已驳回
      backgroundColor = '#FEE2E2';
      color = '#DC2626';
      break;
    case 3: // 提现成功
      backgroundColor = '#D1FAE5';
      color = '#059669';
      break;
    case 4: // 提现失败
      backgroundColor = '#FEE2E2';
      color = '#DC2626';
      break;
    default:
      backgroundColor = '#F3F4F6';
      color = '#6B7280';
  }

  return {
    backgroundColor,
    color,
    fontWeight: 500,
    fontSize: '12px',
    height: '24px',
    borderRadius: '12px',
  };
});

const WithdrawList = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [withdrawList, setWithdrawList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 筛选选项
  const filterOptions = ['All', 'Today', 'This Week', 'This Month'];

  // 获取提现记录列表
  const fetchWithdrawList = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        pageSize: 20,
        pageNum: 1,
        ...(getDateTypeFromFilter(activeFilter) && { dateType: getDateTypeFromFilter(activeFilter) }),
      };

      withdrawRecord(params, (result) => {
        const { code, rows, msg } = result;
        if (code === 200) {
          setWithdrawList(rows || []);
        } else {
          showMessage(msg || 'Failed to fetch withdrawal records', 'error');
        }
      });
    } catch (error) {
      showMessage('Network error, please try again later', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchWithdrawList();
  }, [fetchWithdrawList]);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);



  // 处理搜索
  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  // 处理筛选变化
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  // 跳转到详情页
  const handleViewDetails = (withdraw) => {
    navigate('/campaigns/invitations/withdraw-detail', {
      state: {
        withdrawData: withdraw
      }
    });
  };

  // 过滤搜索结果 - 使用防抖后的搜索文本
  const filteredWithdrawList = useMemo(() => {
    return withdrawList.filter(item => {
      if (!debouncedSearchText) return true;
      const searchLower = debouncedSearchText.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.withdrawId?.toString().includes(searchLower) ||
        item.accountNo?.toLowerCase().includes(searchLower)
      );
    });
  }, [withdrawList, debouncedSearchText]);

  return (
    <Container maxWidth="sm" sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <Box sx={{ pt: 2, pb: 4, px: 2 }}>
        {/* Header */}
        {!isInApp() && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            position: 'relative'
          }}>
            <IconButton
              sx={{
                position: 'absolute',
                left: -8,
                padding: '8px'
              }}
              onClick={() => navigate(-1)}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: 17,
                fontWeight: 600,
                color: '#000',
                textAlign: 'center',
                width: '100%'
              }}
            >
              Reward Payout Center
            </Typography>
          </Box>
        )}

        {/* Search Bar */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <SearchIcon sx={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9CA3AF',
            fontSize: 20,
            zIndex: 1
          }} />
          <SearchBar
            fullWidth
            placeholder="Search orders..."
            value={searchText}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ width: 40 }}>
                  {/* 占位符，图标通过绝对定位显示 */}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Filter Chips */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 3,
            '& .MuiChip-root': {
              cursor: 'pointer'
            }
          }}
        >
          {filterOptions.map((filter) => (
            <FilterChip
              key={filter}
              label={filter}
              active={(filter === activeFilter).toString()}
              onClick={() => handleFilterChange(filter)}
            />
          ))}
        </Stack>

        {/* Withdraw List */}
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        ) : filteredWithdrawList.length > 0 ? (
          filteredWithdrawList.map((withdraw) => (
            <Card
              key={withdraw.withdrawId}
              sx={{
                mb: 2,
                p: 3,
                borderRadius: '12px',
                background: '#FFFFFF',
                border: '1px solid #F3F4F6',
                boxShadow: 'none',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }}
              onClick={() => handleViewDetails(withdraw)}
            >
              {/* Order Header */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2
              }}>
                <Typography sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#000'
                }}>
                  Order {withdraw.withdrawId}
                </Typography>
                <StatusChip
                  label={getStatusLabel(withdraw.status)}
                  status={withdraw.status}
                />
              </Box>

              {/* Order Details */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                    Customer ID
                  </Typography>
                  <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500 }}>
                    {withdraw.userId || '--'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                    Customer
                  </Typography>
                  <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500 }}>
                    {withdraw.name || '--'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                    Time
                  </Typography>
                  <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500 }}>
                    {formatTime(withdraw.createTime)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                    Amount
                  </Typography>
                  <Typography sx={{
                    color: '#000',
                    fontSize: '16px',
                    fontWeight: 600
                  }}>
                    {formatAmount(withdraw.price)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))
        ) : (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            color: '#6B7280'
          }}>
            <Typography sx={{ fontSize: '16px', mb: 1 }}>
              No withdraw records found
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {debouncedSearchText ? 'Try adjusting your search terms' : 'Withdraw records will appear here'}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default WithdrawList;